import startCase from 'lodash/startCase'

import type {
  SubscriptionAddOnCharge,
  SubscriptionChargebeeData,
} from 'types/subscription'

import type { ActiveAddOn, Price } from './add-ons-row'
import type { BillingPeriod } from './billing-period-row'

type ChargebeeSubscription = NonNullable<SubscriptionChargebeeData['chargebee']>

export const ADMIN_ONLY_MESSAGE =
  'Only admins on your account can make changes to your plan.'

export const PRICING_PAGE_ERROR_MESSAGE =
  'We couldn’t load the pricing page. Refresh the page to try again.'

export type CardDetails = {
  /** e.g. "Visa", or null when Chargebee doesn't say */
  brand: string | null
  last4: string | null
}

/**
 * Formats a Chargebee amount in its own currency for the given locale, dropping zero cents,
 * e.g. "A$380" for AUD and "$380" for USD in English.
 */
export const formatBillingAmount = (
  cents: number,
  currencyCode: string | null | undefined,
  locale: string,
): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode || 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100)

/** The card on file, or null without one. */
export const getCardDetails = (
  subscription: Pick<
    ChargebeeSubscription,
    'card_type' | 'last_segment_of_card_number'
  >,
): CardDetails | null => {
  const { card_type, last_segment_of_card_number } = subscription
  if (!card_type && !last_segment_of_card_number) return null

  return {
    brand: card_type ? startCase(card_type) : null,
    last4: last_segment_of_card_number || null,
  }
}

const formatCountry = (countryCode: string, locale: string): string => {
  try {
    return (
      new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode) ??
      countryCode
    )
  } catch {
    return countryCode
  }
}

/** Builds the "Billed to" lines: name, email, street and city, then country. */
export const getBilledToLines = (
  subscription: Pick<
    ChargebeeSubscription,
    | 'first_name'
    | 'last_name'
    | 'email'
    | 'address'
    | 'address_2'
    | 'city'
    | 'state'
    | 'zip'
    | 'country'
  >,
  locale: string,
): string[] => {
  const name = [subscription.first_name, subscription.last_name]
    .filter(Boolean)
    .join(' ')
  const street = [subscription.address, subscription.address_2]
    .filter(Boolean)
    .join(', ')
  const locality = [subscription.city, subscription.state, subscription.zip]
    .filter(Boolean)
    .join(' ')
  const streetAndLocality = [street, locality].filter(Boolean).join(', ')

  return [
    name,
    subscription.email,
    streetAndLocality,
    subscription.country && formatCountry(subscription.country, locale),
  ].filter((line): line is string => Boolean(line))
}

const MONTHS_PER_UNIT = { month: 1, year: 12 } as const

const chargeMonths = (charge: SubscriptionAddOnCharge): number =>
  charge.billing_period * MONTHS_PER_UNIT[charge.billing_period_unit]

const getPaidCharges = (
  charges: SubscriptionAddOnCharge[],
): SubscriptionAddOnCharge[] =>
  charges.filter(charge => charge.amount_in_cents > 0)

// Converts each charge to the same number of months first, so mixed periods add up correctly
const sumInMonths = (
  charges: SubscriptionAddOnCharge[],
  months: number,
): number =>
  Math.round(
    charges.reduce(
      (sum, charge) =>
        sum + (charge.amount_in_cents * months) / chargeMonths(charge),
      0,
    ),
  )

// Sums the charges into the period of the first one
const getChargesPrice = (
  charges: SubscriptionAddOnCharge[],
  currencyCode: string | null | undefined,
  locale: string,
): Price | null => {
  const paid = getPaidCharges(charges)
  const [first] = paid
  if (!first) return null

  return {
    amount: formatBillingAmount(
      sumInMonths(paid, chargeMonths(first)),
      currencyCode,
      locale,
    ),
    billingPeriod: first.billing_period,
    billingPeriodUnit: first.billing_period_unit,
  }
}

/** Totals the add-on charges in the plan's billing period. */
export const getAddOnsTotal = ({
  charges,
  period,
  currencyCode,
  locale,
}: {
  charges: SubscriptionAddOnCharge[]
  period: BillingPeriod | null
  currencyCode: string | null | undefined
  locale: string
}): Price | null => {
  const paid = getPaidCharges(charges)
  if (!period || !paid.length) return null

  return {
    amount: formatBillingAmount(
      sumInMonths(paid, period === 'annual' ? 12 : 1),
      currencyCode,
      locale,
    ),
    billingPeriod: 1,
    billingPeriodUnit: period === 'annual' ? 'year' : 'month',
  }
}

/** Whether the subscription is charged for the add-on, which is what makes it owned. */
export const hasAddOnCharge = (
  charges: SubscriptionAddOnCharge[],
  addon: SubscriptionAddOnCharge['addon'],
): boolean => charges.some(charge => charge.addon === addon)

/**
 * Builds a line per add-on the subscription is charged for, in display order.
 * Chargebee decides ownership and price; usage comes from DISCO, which Chargebee doesn't track.
 */
export const getActiveAddOns = ({
  charges,
  currencyCode,
  locale,
  discoverySuiteQuota,
  discoverySuiteUsed,
  watermarkingQuota,
  watermarkingUsed,
  watermarkingUnlimited,
}: {
  charges: SubscriptionAddOnCharge[]
  currencyCode: string | null | undefined
  locale: string
  discoverySuiteQuota: number
  discoverySuiteUsed: number
  watermarkingQuota: number
  watermarkingUsed: number
  watermarkingUnlimited: boolean
}): ActiveAddOn[] => {
  const lineFor = (
    addon: SubscriptionAddOnCharge['addon'],
    used: number,
    allowance: number,
  ): ActiveAddOn => ({
    addon,
    usage: allowance > 0 ? { used, allowance } : null,
    price: getChargesPrice(
      charges.filter(charge => charge.addon === addon),
      currencyCode,
      locale,
    ),
  })
  const addOns: ActiveAddOn[] = []

  if (hasAddOnCharge(charges, 'discovery_suite')) {
    addOns.push(
      lineFor('discovery_suite', discoverySuiteUsed, discoverySuiteQuota),
    )
  }
  if (hasAddOnCharge(charges, 'watermarking')) {
    addOns.push(
      lineFor(
        'watermarking',
        watermarkingUsed,
        watermarkingUnlimited ? Infinity : watermarkingQuota,
      ),
    )
  }

  return addOns
}
